/**
 * El DISCO: cómo se dibuja un jugador en la cancha.
 *
 * Sale de `main.ts` por el mismo motivo que el cartel del gol: `main.ts` arranca
 * el juego al importarlo, así que nada podía medir ni mirar este dibujo sin
 * abrir una partida. Y aquí hay algo que medir — ver `bench/discs.html`.
 *
 * Es una función PURA de dibujo: recibe el estado y pinta. No lee ajustes, no
 * guarda nada y no sabe qué es una partida.
 */
import { CHARGE_GROW_TIME, CHARGE_RANGE_PAD, PLAYER_R, POSSESSION_TIME_MAX, type GameState } from "@tak/sim";
import type { TeamStyle } from "../a11y";

/**
 * Aspecto de un disco: aro de carga, cuerpo, marca de equipo y borde.
 *
 * La CARGA se cuenta en segundos de posesión, no en un ratio, porque se lee en
 * dos tramos con significados distintos (ver `CHARGE_GROW_TIME` en @tak/sim):
 *
 *  - 0 → 3 s: el aro CRECE. El tamaño dice cuánto llevas cargado.
 *  - 3 → 4 s: el aro ya no crece; se ENCIENDE (brillo blanco y halo). Es la
 *    señal de "ya está cargada del todo" — al llegar a 4 s la patada es máxima.
 *
 * Un solo aro creciendo hasta el final obligaba a medir a ojo cuándo estaba
 * lleno; con dos señales distintas para dos cosas distintas se ve sin pensar.
 */
export function drawPlayer(
  ctx: CanvasRenderingContext2D,
  w: GameState["world"],
  body: number,
  style: TeamStyle,
  charging: boolean,
  possession: number,
  kickPulse: number,
  kickArmed: boolean,
  icon = "",
): void {
  const px = w.px[body];
  const py = w.py[body];
  const color = style.color;

  if (charging && possession > 0) {
    // Tramo de crecimiento (0..1) y tramo de brillo (0..1, solo tras llenarse).
    const grow = Math.min(1, possession / CHARGE_GROW_TIME);
    const glow = Math.max(0, Math.min(1,
      (possession - CHARGE_GROW_TIME) / Math.max(1e-6, POSSESSION_TIME_MAX - CHARGE_GROW_TIME)));
    const outerR = PLAYER_R + CHARGE_RANGE_PAD * grow;
    const innerR = outerR - 10;
    ctx.beginPath();
    ctx.arc(px, py, outerR, 0, Math.PI * 2);
    ctx.arc(px, py, innerR, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fillStyle = `rgba(255,255,255,${0.18 + 0.22 * grow + 0.35 * glow})`;
    ctx.fill();
    ctx.save();
    if (glow > 0) {
      // Cargada del todo: el aro se enciende. El halo crece con el tramo final,
      // así que el instante exacto de "máxima" es visible sin mirar un número.
      ctx.shadowColor = "rgba(255,255,255,0.95)";
      ctx.shadowBlur = 6 + 14 * glow;
    }
    ctx.beginPath();
    ctx.arc(px, py, outerR, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(255,255,255,${0.55 + 0.25 * grow + 0.2 * glow})`;
    ctx.lineWidth = 2 + 1.5 * glow;
    ctx.stroke();
    ctx.restore();
  }

  ctx.save();
  ctx.shadowColor = color;
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.arc(px, py, w.radius[body], 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
  // MOB-3 · La MARCA de equipo: anillo continuo (equipo 0) contra anillo partido
  // en cuatro (equipo 1). Es lo que hace el juego jugable sin depender del color
  // — funciona igual en escala de grises y no hay que memorizar nada, porque el
  // anillo se ve en el mismo sitio que el color al que sustituye.
  const r = w.radius[body];
  // CONTORNO: una línea CERRADA en el borde del disco. Negra de serie (recorta
  // el disco contra la cancha) y blanca encendida mientras se pulsa patear —
  // que es la única señal de "voy a golpear" visible desde fuera.
  //
  // Va SEPARADO de la marca de equipo. Antes eran la misma línea, así que en el
  // equipo turquesa —cuya marca de accesibilidad es un anillo PARTIDO en cuatro—
  // el contorno negro salía a trozos y el disco parecía roto. Una cosa es "de
  // qué equipo soy" (marca) y otra "estoy armado" (contorno).
  //
  // ARMADO, no "pulsando". El disparo se gasta al golpear aunque se siga
  // apretando la tecla (una pulsación = una patada), así que encender el marco
  // mientras hay dedo encima mentía: se quedaba blanco después de haber pegado,
  // como si aún quedara algo cargado. Ahora el marco es exactamente lo que queda
  // por disparar, y se apaga en el golpe.
  const kicking = kickArmed;
  ctx.save();
  if (kicking) {
    ctx.shadowColor = "rgba(255,255,255,0.95)";
    ctx.shadowBlur = 9;
  }
  ctx.lineWidth = kicking ? 3 : 2.5;
  ctx.strokeStyle = kicking ? "rgba(255,255,255,0.98)" : "rgba(0,0,0,0.9)";
  ctx.beginPath();
  ctx.arc(px, py, r + 0.5, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  // MARCA DE EQUIPO (MOB-3 · accesibilidad): anillo continuo contra anillo
  // partido, por dentro del contorno. Funciona en escala de grises y no
  // depende del color. Con las marcas desactivadas en ACCESO, el disco va liso.
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = "rgba(255,255,255,0.8)";
  const rm = r - 2.5;
  if (style.marker !== "none") {
    ctx.beginPath();
    ctx.arc(px, py, rm, 0, Math.PI * 2);
    ctx.stroke();
  }
  // El segundo equipo lleva un SEGUNDO anillo concéntrico. Antes su marca era un
  // anillo partido en cuatro y la gente lo reportaba como un disco roto — una
  // señal de accesibilidad que se lee como un bug ya no es una señal.
  if (style.marker === "double") {
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(px, py, rm - 5, 0, Math.PI * 2);
    ctx.stroke();
  }
  // Brillo central… o el ICONO del perfil, si lo hay. El icono ocupa el sitio
  // del brillo en vez de sumarse: dos manchas claras en el mismo punto y el
  // dibujo se vuelve papilla.
  if (icon) {
    ctx.save();
    // Opacidad PROPIA. El icono se pintaba con el `fillStyle` que hubiera
    // quedado del aro de carga, así que su transparencia subía y bajaba con la
    // potencia de la patada: cargar el disparo hacía que tu icono parpadeara.
    // Son dos cosas sin ninguna relación y ahora cada una fija la suya.
    ctx.globalAlpha = 1;
    ctx.fillStyle = "#ffffff";
    ctx.font = `${Math.round(r * 1.15)}px "Segoe UI Emoji","Noto Color Emoji",system-ui,sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(0,0,0,0.55)";
    ctx.shadowBlur = 3;
    ctx.fillText(icon, px, py + r * 0.04);
    ctx.restore();
  } else {
    ctx.beginPath();
    ctx.arc(px, py, r * 0.42, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.24)";
    ctx.fill();
  }

  if (kickPulse > 0) {
    ctx.beginPath();
    ctx.arc(px, py, w.radius[body] + (1 - kickPulse) * 22, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(255,255,255,${kickPulse * 0.7})`;
    ctx.lineWidth = 3;
    ctx.stroke();
  }
}
